import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { ScreenHeader } from '../../components/ScreenHeader';
import { normalizeApiError } from '../../api/apiError';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { createOrganization } from '../../services/managedOrganizationService';
import type { OrganizationCreateInput } from '../../types/managedOrganization';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  COUNTRY_CHOICES,
  CURRENCY_CHOICES,
  DATE_FORMAT_CHOICES,
  TIME_FORMAT_CHOICES,
  TIME_ZONE_CHOICES,
  type OrganizationChoice,
} from '../../config/organizationCreationOptions';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationCreate'>;
type Form = Record<keyof OrganizationCreateInput, string>;
const empty: Form = {
  organizationCode: '', organizationName: '', legalName: '', registrationNumber: '',
  taxRegistrationNumber: '', officialEmail: '', phoneNumber: '', websiteUrl: '',
  addressLine1: '', addressLine2: '', city: '', stateName: '', countryCode: '',
  postalCode: '', timeZone: '', defaultCurrencyCode: '', defaultDateFormat: '',
  defaultTimeFormat: '', logoUrl: '',
};

const fields: Array<{ key: keyof Form; label: string; required?: boolean; email?: boolean }> = [
  { key: 'organizationCode', label: 'Organization Code *', required: true },
  { key: 'organizationName', label: 'Organization Name *', required: true },
  { key: 'legalName', label: 'Legal Name' },
  { key: 'officialEmail', label: 'Official Email *', required: true, email: true },
  { key: 'registrationNumber', label: 'Registration Number' },
  { key: 'taxRegistrationNumber', label: 'Tax Registration Number' },
  { key: 'phoneNumber', label: 'Phone' },
  { key: 'websiteUrl', label: 'Website' },
  { key: 'addressLine1', label: 'Address Line 1' },
  { key: 'addressLine2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'stateName', label: 'State' },
  { key: 'postalCode', label: 'Postal Code' },
  { key: 'logoUrl', label: 'Logo URL' },
];

const choiceFields: Array<{
  key: 'countryCode' | 'timeZone' | 'defaultCurrencyCode' | 'defaultDateFormat' | 'defaultTimeFormat';
  label: string;
  choices: OrganizationChoice[];
}> = [
  { key: 'countryCode', label: 'Country *', choices: COUNTRY_CHOICES },
  { key: 'timeZone', label: 'Time Zone *', choices: TIME_ZONE_CHOICES },
  { key: 'defaultCurrencyCode', label: 'Currency *', choices: CURRENCY_CHOICES },
  { key: 'defaultDateFormat', label: 'Date Format *', choices: DATE_FORMAT_CHOICES },
  { key: 'defaultTimeFormat', label: 'Time Format *', choices: TIME_FORMAT_CHOICES },
];

function filteredOptions(choices: OrganizationChoice[], search: string): SelectorOption[] {
  const query = search.trim().toLowerCase();
  return choices
    .map((choice, index) => ({ choice, id: index + 1 }))
    .filter(({ choice }) => !query || `${choice.value} ${choice.label} ${choice.keywords ?? ''}`
      .toLowerCase().includes(query))
    .map(({ choice, id }) => ({ id, label: choice.label, subtitle: choice.value }));
}

export function OrganizationCreateScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const [form, setForm] = useState<Form>(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  if (!auth.roles?.includes('SUPER_ADMIN')) return <UnauthorizedScreen />;

  const submit = async () => {
    const missing = fields.find(field => field.required && !form[field.key].trim());
    if (missing) { setError(`${missing.label.replace(' *', '')} is required.`); return; }
    const missingChoice = choiceFields.find(field => !form[field.key].trim());
    if (missingChoice) { setError(`${missingChoice.label.replace(' *', '')} is required.`); return; }
    if (form.countryCode.trim().length !== 2 || form.defaultCurrencyCode.trim().length !== 3) {
      setError('Country code must be 2 characters and currency code must be 3 characters.'); return;
    }
    setSaving(true);
    try {
      const created = await runOperation('add', () => createOrganization(form));
      navigation.replace(routes.organizationCreated, {
        organizationId: created.organizationId,
        organizationCode: created.organizationCode,
        organizationName: created.organizationName,
      });
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally { setSaving(false); }
  };

  return (
    <AppScreen webLayout="narrow" scroll>
      <ScreenHeader title="Create Organization" onBack={navigation.goBack} />
      <View style={styles.body}>
        <Text style={[styles.section, { color: palette.textPrimary }]}>Organization information</Text>
        {fields.map(field => (
          <AppInput key={field.key} label={field.label} value={form[field.key]}
            onChangeText={value => setForm(current => ({ ...current, [field.key]: value }))}
            autoCapitalize={field.key === 'organizationCode' || field.key === 'countryCode' ||
              field.key === 'defaultCurrencyCode' ? 'characters' : 'none'}
            keyboardType={field.email ? 'email-address' : 'default'} />
        ))}
        <Text style={[styles.section, { color: palette.textPrimary }]}>Localization</Text>
        {choiceFields.map(field => {
          const selected = field.choices.find(choice => choice.value === form[field.key]);
          return (
            <SearchableEntitySelector
              key={field.key}
              label={field.label}
              selectedValue={selected ? field.choices.indexOf(selected) + 1 : null}
              selectedLabel={selected?.label ?? null}
              loadOptions={async search => filteredOptions(field.choices, search)}
              onSelect={option => {
                const choice = field.choices[option.id - 1];
                if (choice) setForm(current => ({ ...current, [field.key]: choice.value }));
              }}
              onClear={() => setForm(current => ({ ...current, [field.key]: '' }))}
              placeholder={`Search ${field.label.replace(' *', '').toLowerCase()}`}
            />
          );
        })}
        {error ? <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>{error}</Text> : null}
        <View style={styles.actions}>
          <AppButton title="Cancel" variant="secondary" onPress={navigation.goBack} style={styles.flex} />
          <AppButton title="Create Organization" onPress={submit} loading={saving} style={styles.flex} />
        </View>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { padding: spacing.lg, gap: spacing.md },
  section: { ...typography.sectionTitle },
  error: { ...typography.caption },
  actions: { flexDirection: 'row', gap: spacing.sm },
  flex: { flex: 1 },
});
